import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogIn, LogOut, Clock, Trash2, Store as StoreIcon, Timer } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (ts) => ts ? new Date(ts).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '';

function SummaryCard({ label, data }) {
  const diff = data?.diff ?? 0;
  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <p className="text-xs uppercase font-bold text-slate-400">{label}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1">{data?.worked_hours ?? 0}<span className="text-sm text-slate-400 font-normal">h</span></p>
        <div className="flex items-center gap-2 mt-1 text-xs">
          <span className="text-slate-400">Prev. {data?.expected_hours ?? 0}h</span>
          <span className={`font-semibold ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {diff >= 0 ? '+' : ''}{diff}h
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PersonalFichajes({ isAdmin, employees }) {
  const [selectedId, setSelectedId] = useState('');
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [storeId, setStoreId] = useState('');
  const [punching, setPunching] = useState(false);
  const [noEmployee, setNoEmployee] = useState(false);

  const selectedEmp = employees.find(e => e.id === selectedId);

  const load = useCallback(async (empId) => {
    try {
      const params = empId ? `?employee_id=${empId}` : '';
      const [st, sm] = await Promise.all([
        axios.get(`${API}/personal/timeclock/status${params}`),
        axios.get(`${API}/personal/timeclock/summary${params}`),
      ]);
      setStatus(st.data);
      setSummary(sm.data);
      setStoreId(st.data.default_store_id || '');
      setNoEmployee(false);
    } catch (e) {
      if (e.response?.status === 400) setNoEmployee(true);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      if (!selectedId && employees.length > 0) { setSelectedId(employees[0].id); return; }
      if (selectedId) load(selectedId);
    } else {
      load('');
    }
  }, [isAdmin, selectedId, employees, load]);

  const punch = async (type) => {
    setPunching(true);
    try {
      const body = { type, store_id: storeId || null };
      if (isAdmin && selectedId) body.employee_id = selectedId;
      await axios.post(`${API}/personal/timeclock/punch`, body);
      toast.success(type === 'in' ? 'Entrada registrada' : 'Salida registrada');
      load(isAdmin ? selectedId : '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al fichar');
    } finally {
      setPunching(false);
    }
  };

  const deleteEntry = async (id) => {
    try {
      await axios.delete(`${API}/personal/timeclock/${id}`);
      toast.success('Fichaje eliminado');
      load(isAdmin ? selectedId : '');
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  if (noEmployee) {
    return (
      <Card className="border-slate-200 border-dashed">
        <CardContent className="py-14 text-center text-slate-500">
          <Clock size={40} className="mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Tu usuario no está enlazado a ninguna ficha de empleado.</p>
          <p className="text-sm">Pide a un administrador que te enlace en la pestaña Empleados.</p>
        </CardContent>
      </Card>
    );
  }

  const inside = status?.inside;
  const stores = selectedEmp ? [] : [];

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-600">Empleado:</span>
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="w-64" data-testid="fichaje-employee-select"><SelectValue placeholder="Selecciona empleado" /></SelectTrigger>
            <SelectContent>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Punch card */}
      <Card className="border-slate-200 overflow-hidden">
        <div className={`h-1.5 ${inside ? 'bg-green-500' : 'bg-slate-300'}`} />
        <CardContent className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${inside ? 'bg-green-100' : 'bg-slate-100'}`}>
              <Timer size={26} className={inside ? 'text-green-600' : 'text-slate-400'} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Estado actual</p>
              <p className="text-xl font-bold text-slate-900" data-testid="fichaje-status">
                {inside ? 'Dentro (jornada abierta)' : 'Fuera'}
              </p>
              {status?.last_punch && (
                <p className="text-xs text-slate-400 mt-0.5">
                  Último: {status.last_punch.type === 'in' ? 'Entrada' : 'Salida'} {fmtTime(status.last_punch.timestamp)}
                  {status.last_punch.store_name ? ` · ${status.last_punch.store_name}` : ''}
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={() => punch(inside ? 'out' : 'in')}
            disabled={punching}
            className={inside ? 'bg-red-600 hover:bg-red-700 h-12 px-6' : 'bg-green-600 hover:bg-green-700 h-12 px-6'}
            data-testid="punch-btn"
          >
            {inside ? <><LogOut size={18} className="mr-2" />Registrar salida</> : <><LogIn size={18} className="mr-2" />Registrar entrada</>}
          </Button>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard label="Hoy" data={summary?.day} />
        <SummaryCard label="Esta semana" data={summary?.week} />
        <SummaryCard label="Este mes" data={summary?.month} />
      </div>

      {/* Today's punches */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock size={16} />Fichajes de hoy</CardTitle></CardHeader>
        <CardContent>
          {(!status?.today_entries || status.today_entries.length === 0) ? (
            <p className="text-sm text-slate-400">Sin fichajes hoy.</p>
          ) : (
            <div className="space-y-2" data-testid="today-punches">
              {status.today_entries.map(e => (
                <div key={e.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-3">
                    <Badge className={e.type === 'in' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {e.type === 'in' ? 'Entrada' : 'Salida'}
                    </Badge>
                    <span className="font-mono font-semibold text-slate-800">{fmtTime(e.timestamp)}</span>
                    {e.store_name && <span className="text-xs text-slate-500 flex items-center gap-1"><StoreIcon size={12} />{e.store_name}</span>}
                  </div>
                  {isAdmin && (
                    <button onClick={() => deleteEntry(e.id)} className="text-slate-400 hover:text-red-600" data-testid={`delete-punch-${e.id}`}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
